import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { 
  Music, User, Mail, Phone, MapPin, Globe, 
  Link as LinkIcon, Upload, Send, Loader2, 
  CheckCircle, X, FileAudio
} from 'lucide-react';
import './DemoSubmissionPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GENRE_OPTIONS = [
  'Electronic', 'House', 'Techno', 'Deep House', 'Tech House',
  'Progressive House', 'Melodic Techno', 'Minimal', 'Trance',
  'Drum & Bass', 'Dubstep', 'Future Bass', 'Hip Hop', 'R&B',
  'Pop', 'Rock', 'Indie', 'Alternative', 'Other'
];

const ARTIST_TYPES = [
  'Solo Artist', 'DJ', 'Producer', 'DJ/Producer', 'Band', 'Group', 'Other'
];

const COUNTRIES = [
  'Canada', 'United States', 'United Kingdom', 'Germany', 'France', 
  'Spain', 'Italy', 'Netherlands', 'Belgium', 'Australia',
  'Brazil', 'Mexico', 'Argentina', 'Japan', 'South Korea', 'Other'
];

const DemoSubmissionPage = () => {
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    artist_name: '',
    real_name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    genre: '',
    artist_type: 'Solo Artist',
    bio: '',
    soundcloud_link: '',
    soundcloud_track_1: '',
    soundcloud_track_2: '',
    soundcloud_track_3: '',
    beatport_link: '',
    spotify_link: '',
    other_links: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate files
    const validFiles = files.filter(file => {
      const isAudio = file.type.startsWith('audio/') || 
                      file.name.toLowerCase().endsWith('.mp3') ||
                      file.name.toLowerCase().endsWith('.wav');
      const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB max
      
      if (!isAudio) {
        toast.error(`${file.name} is not an audio file`);
        return false;
      }
      if (!isValidSize) {
        toast.error(`${file.name} is too large (max 50MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploadingFiles(true);

    for (const file of validFiles) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const res = await fetch(`${API_URL}/api/artist-portal/demos/upload`, {
          method: 'POST',
          body: formDataUpload
        });

        if (res.ok) {
          const data = await res.json();
          setUploadedFiles(prev => [...prev, {
            url: data.file_url,
            name: file.name,
            size: (file.size / (1024 * 1024)).toFixed(2) + ' MB'
          }]);
          toast.success(`Uploaded: ${file.name}`);
        } else {
          const error = await res.json();
          toast.error(error.detail || 'Upload failed');
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploadingFiles(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.artist_name || !formData.email || !formData.genre) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.soundcloud_link && uploadedFiles.length === 0) {
      toast.error('Please provide a SoundCloud link or upload at least one demo file');
      return;
    }

    setLoading(true);

    try {
      // Build demo_link - prefer SoundCloud, otherwise use the first uploaded file URL
      const demoLink = formData.soundcloud_link || 
                       (uploadedFiles.length > 0 ? uploadedFiles[0].url : '');
      
      // Combine all social/streaming links into one string
      const socialLinks = [
        formData.spotify_link && `Spotify: ${formData.spotify_link}`,
        formData.beatport_link && `Beatport: ${formData.beatport_link}`,
        formData.other_links
      ].filter(Boolean).join(' | ');

      // Use the simpler /api/demos/submit endpoint that already has email notifications
      const soundcloudTracks = [
        formData.soundcloud_track_1,
        formData.soundcloud_track_2,
        formData.soundcloud_track_3,
      ].filter(Boolean);

      const submitData = {
        artist_name: formData.artist_name,
        email: formData.email,
        genre: formData.genre,
        demo_link: demoLink,
        message: formData.message || `Type: ${formData.artist_type}, Country: ${formData.country || 'N/A'}, City: ${formData.city || 'N/A'}${formData.bio ? ', Bio: ' + formData.bio : ''}`,
        social_links: socialLinks || null,
        soundcloud_tracks: soundcloudTracks.length > 0 ? soundcloudTracks : null
      };

      const res = await fetch(`${API_URL}/api/demos/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      if (res.ok) {
        setSubmitted(true);
        toast.success('Demo submitted successfully!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Submission failed');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit demo');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="demo-submission-page">
        <div className="submission-success">
          <div className="success-icon">
            <CheckCircle size={64} />
          </div>
          <h1>Thank You!</h1>
          <p>Your demo has been submitted successfully.</p>
          <p className="success-detail">
            Our team will review your submission and get back to you at <strong>{formData.email}</strong>.
            This usually takes 2-4 weeks.
          </p>
          <a href="/" className="back-home-btn">
            Back to Homepage
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="demo-submission-page">
      <div className="demo-hero">
        <h1>Submit Your Demo</h1>
        <p>Show us what you've got. We're always looking for fresh talent to join the AMR family.</p>
      </div>

      <div className="demo-form-container">
        <form onSubmit={handleSubmit} className="demo-form">
          {/* Personal Information */}
          <section className="form-section">
            <h2>About You</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>
                  <Music size={16} />
                  Artist / Stage Name *
                </label>
                <input
                  type="text"
                  name="artist_name"
                  value={formData.artist_name}
                  onChange={handleChange}
                  required
                  placeholder="Your artist name"
                  data-testid="demo-artist-name"
                />
              </div>

              <div className="form-group">
                <label>
                  <User size={16} />
                  Real Name
                </label>
                <input
                  type="text"
                  name="real_name"
                  value={formData.real_name}
                  onChange={handleChange}
                  placeholder="Your legal name (optional)"
                  data-testid="demo-real-name"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <Mail size={16} />
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your@email.com"
                  data-testid="demo-email"
                />
              </div>

              <div className="form-group">
                <label>
                  <Phone size={16} />
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                  data-testid="demo-phone"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <Globe size={16} />
                  Country
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  data-testid="demo-country"
                >
                  <option value="">Select country</option>
                  {COUNTRIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>
                  <MapPin size={16} />
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Your city"
                  data-testid="demo-city"
                />
              </div>
            </div>
          </section>

          {/* Music Information */}
          <section className="form-section">
            <h2>Your Music</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>
                  <Music size={16} />
                  Primary Genre *
                </label>
                <select
                  name="genre"
                  value={formData.genre}
                  onChange={handleChange}
                  required
                  data-testid="demo-genre"
                >
                  <option value="">Select genre</option>
                  {GENRE_OPTIONS.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>
                  <User size={16} />
                  Artist Type
                </label>
                <select
                  name="artist_type"
                  value={formData.artist_type}
                  onChange={handleChange}
                  data-testid="demo-artist-type"
                >
                  {ARTIST_TYPES.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group full-width">
              <label>Bio / About Your Music</label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                placeholder="Tell us about yourself and your music journey..."
                rows={4}
                data-testid="demo-bio"
              />
            </div>
          </section>

          {/* Links */}
          <section className="form-section">
            <h2>Your Links</h2>
            <p className="section-note">
              Provide at least one streaming link so we can hear your music.
            </p>
            
            <div className="form-group full-width">
              <label>
                <LinkIcon size={16} />
                SoundCloud Profile Link
              </label>
              <input
                type="url"
                name="soundcloud_link"
                value={formData.soundcloud_link}
                onChange={handleChange}
                placeholder="https://soundcloud.com/your-profile"
                data-testid="demo-soundcloud"
              />
            </div>

            <div className="form-group full-width">
              <label>
                <Music size={16} />
                SoundCloud Tracks to Review (up to 3)
              </label>
              <p className="section-note" style={{ marginTop: 0, marginBottom: 8 }}>
                Paste links to specific tracks you want us to listen to
              </p>
              <input
                type="url"
                name="soundcloud_track_1"
                value={formData.soundcloud_track_1}
                onChange={handleChange}
                placeholder="https://soundcloud.com/artist/track-1"
                style={{ marginBottom: 8 }}
                data-testid="demo-sc-track-1"
              />
              <input
                type="url"
                name="soundcloud_track_2"
                value={formData.soundcloud_track_2}
                onChange={handleChange}
                placeholder="https://soundcloud.com/artist/track-2"
                style={{ marginBottom: 8 }}
                data-testid="demo-sc-track-2"
              />
              <input
                type="url"
                name="soundcloud_track_3"
                value={formData.soundcloud_track_3}
                onChange={handleChange}
                placeholder="https://soundcloud.com/artist/track-3"
                data-testid="demo-sc-track-3"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <LinkIcon size={16} />
                  Spotify Link
                </label>
                <input
                  type="url"
                  name="spotify_link"
                  value={formData.spotify_link}
                  onChange={handleChange}
                  placeholder="https://open.spotify.com/artist/..."
                  data-testid="demo-spotify"
                />
              </div>

              <div className="form-group">
                <label>
                  <LinkIcon size={16} />
                  Beatport Link
                </label>
                <input
                  type="url"
                  name="beatport_link"
                  value={formData.beatport_link}
                  onChange={handleChange}
                  placeholder="https://www.beatport.com/artist/..."
                  data-testid="demo-beatport"
                />
              </div>
            </div>

            <div className="form-group full-width">
              <label>
                <LinkIcon size={16} />
                Other Links
              </label>
              <input
                type="text"
                name="other_links"
                value={formData.other_links}
                onChange={handleChange}
                placeholder="Instagram, YouTube, other streaming platforms..."
                data-testid="demo-other-links"
              />
            </div>
          </section>

          {/* File Upload */}
          <section className="form-section">
            <h2>Upload Your Demo</h2>
            <p className="section-note">
              Upload your best tracks (MP3 or WAV, max 50MB each). We recommend 2-3 tracks.
            </p>
            
            <div 
              className={`file-upload-zone ${uploadingFiles ? 'uploading' : ''}`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,.mp3,.wav"
                multiple
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                data-testid="demo-file-input"
              />
              
              {uploadingFiles ? (
                <>
                  <Loader2 size={32} className="spinner" />
                  <p>Uploading...</p>
                </>
              ) : (
                <>
                  <Upload size={32} />
                  <p>Click or drag files here to upload</p>
                  <span>MP3 or WAV, max 50MB</span>
                </>
              )}
            </div>

            {uploadedFiles.length > 0 && (
              <div className="uploaded-files">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="uploaded-file">
                    <FileAudio size={20} />
                    <div className="file-info">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{file.size}</span>
                    </div>
                    <button 
                      type="button"
                      className="remove-file"
                      onClick={() => removeFile(index)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Message */}
          <section className="form-section">
            <h2>Additional Message</h2>
            
            <div className="form-group full-width">
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Anything else you'd like to tell us? Why do you want to join AMR?"
                rows={4}
                data-testid="demo-message"
              />
            </div>
          </section>

          {/* Submit */}
          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
              data-testid="demo-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="spinner" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Submit Demo
                </>
              )}
            </button>
            <p className="form-disclaimer">
              By submitting, you confirm that you own the rights to the music you're sharing.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DemoSubmissionPage;
